<template>
    <div class="mobile-table-item" :class="{
        'expanded': expanded,
        [`rank-${rank}`]: true,
        'podium-item': isPodium
    }" @click="$emit('toggle-details')">
        <!-- Podium Badge for top 3 -->
        <div v-if="isPodium" class="podium-badge">
            <i class="fa-regular fa-medal"></i>
            <span>{{ getPodiumText(rank) }}</span>
        </div>

        <div class="item-header">
            <div class="rank-container">
                <p :class="['rank-number', getRankClass(rank)]">{{ rank }}</p>
                <div v-if="previousPlace !== null && rank !== previousPlace" class="rank-change">
                    <v-icon :color="(rank > previousPlace) ? 'error' : 'primary'" x-small>
                        {{ (rank > previousPlace) ? 'fa-caret-down' : 'fa-caret-up' }}
                    </v-icon>
                    <span :style="`color: ${(rank > previousPlace) ? '#EF5350' : 'var(--v-mainGreen-base)'}`"
                        class="should-blur">{{ Math.abs(rank - previousPlace) }}</span>
                </div>
            </div>

            <div class="user-info">
                <div class="username-container">
                    <span v-if="showMinimalView && !showIbNumber" class="ranking-label">{{ $t("account_NewFrontPage") }}:</span>
                    <span v-if="!showMinimalView" class="should-blur">{{ item.username }}</span>
                    <span v-else-if="!showIbNumber" class="account-id">{{ item.brokerAccountIdentifier || '---' }}</span>
                    <span v-if="rank <= totalAwards" :class="['award-badge', getRankClass(rank)]">
                    </span>
                </div>
                <div v-if="showScore" class="profit-container">
                    <span class="ranking-label">{{ rankingHeader }}:</span>
                    <span class="profit-chip">{{ formattedScore || (item.score !== null ? item.score.toFixed(2) : '-')
                        }}</span>
                </div>
                <div v-if="showIbNumber" class="profit-container">
                    <span class="ranking-label">{{ $t('ibNumber') }}:</span>
                    <span class="profit-chip">{{ ibNumber ?? item.ibNumber ?? item.ib_number ?? '---' }}</span>
                </div>
                <div v-if="showTradingVolUsd" class="profit-container">
                    <span class="ranking-label">{{ $t('trading_Vol') }}:</span>
                    <span class="profit-chip">{{ formatTradingVolumeUsd(item.tradingVolumeUsd) }}</span>
                </div>
                <div v-if="showScoreBracket" class="profit-container">
                    <span class="ranking-label">{{ $t('trading_Vol_Lots') }}:</span>
                    <span class="profit-chip">{{ item.scoreBracket != null ? Number(item.scoreBracket).toFixed(2) : '-' }}</span>
                </div>
                <div v-if="showRegistrationDate && item.registration_date" class="profit-container">
                    <span class="ranking-label">{{ $t('registrationDate') }}:</span>
                    <span class="profit-chip">{{ formatRegistrationDate(item.registration_date) }}</span>
                </div>
            </div>
        </div>

        <div v-if="!showMinimalView" class="item-content">
            <div class="profile-section">
                <a :href="`/profile/${item.username}`" class="profile-link should-blur" @click.stop>
                    <img :src="item.profilePicture || 'https://upload.wikimedia.org/wikipedia/commons/d/d3/User_Circle.png'"
                        class="profile-image">
                    <Flag class="flag" :code="countryCode" />
                </a>
                <span class="name should-blur">{{ item.firstName ?
                    item.firstName.split(' ')[0] : '' }}</span>
            </div>

            <div class="award-section" v-if="rank <= totalAwards">
                <span :class="['winning-chip', getRankClass(rank)]">
                    <p class="award-title">{{ awardTitle }}</p>
                </span>
            </div>
        </div>

        <transition name="slide-fade">
            <div v-if="!showMinimalView && expanded" class="details-container">
                <div v-if="loading" class="spinner"></div>
                <div v-else class="details-content">
                    <slot name="details"></slot>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
export default {
    name: 'MobileTableItem',
    props: {
        item: {
            type: Object,
            required: true
        },
        rank: {
            type: Number,
            required: true
        },
        previousPlace: {
            type: Number,
            default: null
        },
        expanded: {
            type: Boolean,
            default: false
        },
        loading: {
            type: Boolean,
            default: false
        },
        countryCode: {
            type: String,
            default: null
        },
        totalAwards: {
            type: Number,
            default: 0
        },
        awardTitle: {
            type: String,
            default: 'No Award'
        },
        rankingType: {
            type: String,
            default: 'BALANCE'
        },
        isPodium: {
            type: Boolean,
            default: false
        },
        rankingHeader: {
            type: String,
            default: 'Profit'
        },
        formattedScore: {
            type: String,
            default: null
        },
        showMinimalView: {
            type: Boolean,
            default: false
        },
        showScoreBracket: {
            type: Boolean,
            default: false
        },
        showTradingVolUsd: {
            type: Boolean,
            default: false
        },
        showScore: {
            type: Boolean,
            default: true
        },
        showIbNumber: {
            type: Boolean,
            default: false
        },
        ibNumber: {
            type: [String, Number],
            default: null
        },
        showRegistrationDate: {
            type: Boolean,
            default: false
        }
    },
    methods: {
        formatRegistrationDate(dateStr) {
            if (!dateStr) return '---';
            try {
                const date = new Date(dateStr);
                return date.toLocaleDateString(this.$i18n.locale, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                });
            } catch {
                return '---';
            }
        },
        getRankClass(rank) {
            switch (rank) {
                case 1:
                    return 'gold';
                case 2:
                    return 'silver';
                case 3:
                    return 'bronze';
                default:
                    return 'normal';
            }
        },
        getPodiumText(rank) {
            switch (rank) {
                case 1:
                    return this.$t('gold');
                case 2:
                    return this.$t('silver');
                case 3:
                    return this.$t('bronze');
                default:
                    return '';
            }
        },
        formatTradingVolumeUsd(value) {
            if (value == null) return '-';
            const num = Number(value);
            if (Number.isNaN(num)) return '-';
            return new Intl.NumberFormat(this.$i18n.locale, { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
        }
    }
}
</script>



<style lang="scss" scoped>
.mobile-table-item {
    background-color: var(--v-fullWhite-base);
    border-radius: 15px;
    border: 1px solid #9a9a9a;
    margin-bottom: 15px;
    padding: 15px;
    padding-top: 40px;
    position: relative;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    transition: all 0.3s ease;

    &.rank-1,
    &.rank-2,
    &.rank-3 {
        border-width: 2px;
    }

    &.rank-1 {
        border-color: #FFD700;
    }

    &.rank-2 {
        border-color: #C0C0C0;
    }

    &.rank-3 {
        border-color: #CD7F32;
    }

    &.expanded {
        margin-bottom: 25px;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }

    &.podium-item {
        border-width: 2px;
        position: relative;
        margin-top: 15px;
        box-shadow: 0 3px 8px rgba(0, 0, 0, 0.1);

        &.rank-1 {
            border-color: #FFD700;
            background-color: rgba(255, 215, 0, 0.05);
        }

        &.rank-2 {
            border-color: #C0C0C0;
            background-color: rgba(192, 192, 192, 0.05);
        }

        &.rank-3 {
            border-color: #CD7F32;
            background-color: rgba(205, 127, 50, 0.05);
        }
    }

    .podium-badge {
        position: absolute;
        top: -10px;
        right: 10px;
        padding: 3px 10px;
        border-radius: 15px;
        font-size: 12px;
        font-weight: bold;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        display: flex;
        align-items: center;
        gap: 5px;

        i {
            margin-right: 2px;
        }

    }

    &.rank-1 .podium-badge {
        background-color: #FFD700;
    }

    &.rank-2 .podium-badge {
        background-color: #C0C0C0;
    }

    &.rank-3 .podium-badge {
        background-color: #CD7F32;
    }

    .item-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 15px;

        .rank-container {
            display: flex;
            align-items: center;

            .rank-number {
                font-size: 16px;
                font-weight: bold;
                min-width: 30px;
                min-height: 30px;
                width: auto;
                height: auto;
                padding: 4px 8px;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                border-radius: 50%;
                margin-right: 8px;
                aspect-ratio: 1;
                white-space: nowrap;

                &.gold {
                    border: 2px solid #FFD700;
                    color: #FFD700;
                }

                &.silver {
                    border: 2px solid #C0C0C0;
                    color: #C0C0C0;
                }

                &.bronze {
                    border: 2px solid #CD7F32;
                    color: #CD7F32;
                }

                &.normal {
                    border: 1px solid var(--v-mainBlack-base);
                    color: var(--v-mainBlack-base);
                }
            }

            .rank-change {
                display: flex;
                align-items: center;

                span {
                    font-size: 12px;
                    margin-left: 2px;
                }
            }
        }

        .user-info {
            flex-grow: 1;
            margin-left: 10px;
            text-align: right;

            .username-container {
                display: flex;
                flex-direction: column;
                align-items: flex-end;
                margin-bottom: 5px;

                .ranking-label {
                    font-size: 12px;
                    color: #9a9a9a;
                    margin-bottom: 3px;
                }

                span:not(.ranking-label) {
                    font-size: 16px;
                    font-weight: bold;
                }

                .award-badge {
                    margin-left: 5px;
                    font-size: 14px;

                    &.gold {
                        color: #FFD700;
                    }

                    &.silver {
                        color: #C0C0C0;
                    }

                    &.bronze {
                        color: #CD7F32;
                    }
                }
            }

            .profit-container {
                display: flex;
                flex-direction: column;
                align-items: flex-end;

                .ranking-label {
                    font-size: 12px;
                    color: #9a9a9a;
                    margin-bottom: 3px;
                }

                .profit-chip {
                    background-color: var(--v-progressBarBackground-base);
                    padding: 4px 0px;
                    border-radius: 20px;
                    font-size: 14px;
                    font-weight: bold;
                }
            }
        }
    }

    .item-content {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 10px;

        .profile-section {
            display: flex;
            align-items: center;

            .profile-link {
                position: relative;
                margin-right: 10px;

                .profile-image {
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    object-fit: cover;
                }

                .flag {
                    position: absolute;
                    bottom: -2px;
                    right: -2px;
                    width: 16px;
                    height: 16px;
                }
            }

            .name {
                font-size: 14px;
            }
        }

        .award-section {
            margin-left: 20px;

            .winning-chip {
                display: inline-flex;
                align-items: center;
                gap: 5px;
                padding: 3px 12px;
                border-radius: 15px;
                border: 1px solid;
                font-size: 12px;

                &.gold {
                    color: #FFD700;
                    border-color: #FFD700;
                }

                &.silver {
                    color: #C0C0C0;
                    border-color: #C0C0C0;
                }

                &.bronze {
                    color: #CD7F32;
                    border-color: #CD7F32;
                }
            }
        }
    }

    .expand-icon {
        position: absolute;
        bottom: 5px;
        left: 50%;
        transform: translateX(-50%);
        transition: transform 0.3s ease;

        &.expanded {
            transform: translateX(-50%) rotate(180deg);
        }

        i {
            color: #9a9a9a;
        }
    }

    .details-container {
        margin-top: 15px;
        padding-top: 15px;
        border-top: 1px solid #f0f0f0;

        .spinner {
            border: 4px solid rgba(0, 0, 0, 0.1);
            border-left-color: var(--v-mainGreen-base);
            border-radius: 50%;
            width: 30px;
            height: 30px;
            animation: spin 1s linear infinite;
            margin: 0 auto;
        }

        @keyframes spin {
            0% {
                transform: rotate(0deg);
            }

            100% {
                transform: rotate(360deg);
            }
        }
    }

    .award-title {
        text-align: center;
    }
}

.slide-fade-enter-active,
.slide-fade-leave-active {
    transition: all 0.3s ease;
    max-height: 500px;
}

.slide-fade-enter,
.slide-fade-leave-to {
    max-height: 0;
    opacity: 0;
    overflow: hidden;
}
</style>
